interface EventDetail {
    type: keyof typeof EventType;
    name: string;
    value: string;
    group?: string;
    fund?: string;
    fund_type?: string;
    fund_id?: number;
    fundClass?: string;
    shareclass?: string;
    shareclass_code?: string;
    listing?: string;
    listing_code?: string;
    file_type?: string;
    section?: string;
    time?: string;
    old_time?: string;
    pageID?: string;
    position?: string;
    docType?: string;
    url?: string;
    docGroup?: string;
    dropdownValue?: string;
}

interface EventFund {
    name: string;
    type: string;
    id: number;
    fundClass?: string;
}

interface EventShareClass {
    name: string;
    code: string;
}

type EventListing = EventShareClass;

export interface EventDocument {
    type: string;
    name: string;
    date: string;
    url: string;
}

interface EventData {
    fund?: EventFund;
    shareClass?: EventShareClass;
    listing?: EventListing;
}

interface SubscriptionEventData extends EventData {
    cookied?: boolean;
    mode: "watch" | "subscribe";
}

export interface CustomerData {
    email?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    jobTitle?: string;
}
interface DigitalData {
    pageName?: string;
    customer?: CustomerData;
}

enum EventType {
    ButtonClick = "ButtonClick",
    SearchInput = "SearchInput",
    PromoClick = "PromoClick",
    Download = "Download",
    clickLink = "clickLink",
    Dropdown = "Dropdown",
    SubsOverlay = "SubsOverlay",
    SubsSelectMultiple = "SubsSelectMultiple",
    SubsStartShare = "SubsStartShare",
    AccordionClose = "AccordionClose",
    AccordionOpen = "AccordionOpen",
    Checkbox = "Checkbox",
    fieldInteraction = "fieldInteraction",
    pageLoad = "pageLoad",
    Lightbox = "Lightbox",
}

const dispatchEvent = (detail: EventDetail) =>
    document.dispatchEvent(new CustomEvent("fundCentreAction", { detail }));

function filterEvent(
    type: "add" | "remove" | "clear",
    key?: string,
    value?: string,
) {
    dispatchEvent({
        value: value ?? "",
        name:
            type !== "clear"
                ? `Select - Filter - ${key} - ${value} ${
                      type === "remove" ? "- Remove" : ""
                  }`
                : "Clear Filters",
        type: EventType.ButtonClick,
    });
}
function groupEvent(key?: string) {
    dispatchEvent({
        value: key ?? "",
        name: `Select - GroupBy - ${key ?? "- Remove"}`,
        type: EventType.ButtonClick,
    });
}
function expandCollapseEvent(
    data: EventData & {
        group?: string;
        value: "open" | "close";
    },
) {
    dispatchEvent({
        type: EventType.ButtonClick,
        name: `Select - Expand${
            data.group
                ? "Group"
                : data.fund
                  ? "Fund"
                  : data.shareClass
                    ? "ShareClass"
                    : "Listing"
        } - ${
            data.group ??
            data.fund?.name ??
            data.shareClass?.name ??
            data.listing?.name
        } - ${data.value}`,
        value: data.value,
        fund: data.fund?.name,
        fund_id: data.fund?.id,
        fund_type: data.fund?.type,
        fundClass: data.fund?.fundClass,
        shareclass: data.shareClass?.name,
        shareclass_code: data.shareClass?.code,
        group: data.group,
        listing: data.listing?.name,
        listing_code: data.listing?.code,
    });
}
function searchEvent(key?: string) {
    dispatchEvent({
        type: key ? EventType.SearchInput : EventType.ButtonClick,
        name: key ? `Search - ${key}` : "Clear Search",
        value: key ?? "",
    });
}
function downloadDataEvent(
    data: EventData & {
        selection?: boolean;
        name?: string;
        section?: string;
    },
) {
    const { selection, section, name, fund, shareClass } = data;
    dispatchEvent({
        type: EventType.Download,
        name: name ?? (selection ? "Download data" : "All Funds"),
        value: "",
        fund: fund?.name,
        fund_id: fund?.id,
        fund_type: fund?.type,
        fundClass: fund?.fundClass,
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
        section,
    });
}
function bulkDownloadEvent(data: EventData) {
    dispatchEvent({
        type: EventType.Download,
        name: "Bulk download",
        fund: data.fund?.name,
        fund_id: data.fund?.id,
        fund_type: data.fund?.type,
        value: "",
    });
}

function downloadDocumentEvent(data: {
    fund?: EventFund;
    shareClass?: EventShareClass;
    file: EventDocument;
    section?: string;
}) {
    const { fund, shareClass, file, section } = data;
    dispatchEvent({
        type: EventType.Download,
        name: file.name,
        value: file.date,
        docType: file.type,
        file_type: file.type,
        fund: fund?.name,
        fund_id: fund?.id,
        fund_type: fund?.type,
        fundClass: fund?.fundClass,
        section,
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
        url: file.url,
    });
}
function tabEvent({
    key,
    fund,
    section,
    shareClass,
}: EventData & { key: string; section?: string }) {
    dispatchEvent({
        type: EventType.ButtonClick,
        name: `Select - Tab - ${key}`,
        value: key,
        fund: fund?.name,
        fund_id: fund?.id,
        fund_type: fund?.type,
        fundClass: fund?.fundClass,
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
        section,
    });
}
function clickLinkEvent(
    data: EventData & {
        group?: string;
        url?: string;
        name?: string;
        position?: string;
        email?: string;
    },
) {
    const { fund, shareClass, group, url, name = " ", position, email } = data;
    // const customer = email ? { email } : {};
    dispatchEvent({
        type: EventType.clickLink,
        value: url ?? email ?? "",
        fund: fund?.name,
        fund_id: fund?.id,
        fundClass: fund?.fundClass,
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
        group,
        name,
        position,
    });
}
function sectionClickEvent(data: EventData & { section: string }) {
    const { fund, section } = data;
    dispatchEvent({
        type: EventType.ButtonClick,
        value: "",
        section,
        name: `Select - Section - ${section}`,
        fund: fund?.name,
        fund_id: fund?.id,
        fund_type: fund?.type,
        fundClass: fund?.fundClass,
    });
}

function dropdownEvent(
    data: EventData & {
        section?: string;
        value: string;
        name?: string;
        position?: string;
    },
) {
    const { fund, shareClass, section, value, name = "" } = data;
    dispatchEvent({
        type: EventType.Dropdown,
        value,
        dropdownValue: value,
        fund: fund?.name ?? "",
        fund_type: fund?.type ?? "",
        fund_id: fund?.id ?? 0,
        fundClass: fund?.fundClass,
        name,
        section,
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
        position: data.position,
    });
}

function subsOverlayEvent(data: SubscriptionEventData) {
    const { fund, shareClass, mode, cookied } = data;
    const value = `Show fund subscription overlay: mode=${mode}, cookied=${Boolean(
        cookied,
    )}`;
    dispatchEvent({
        type: EventType.SubsOverlay,
        value,
        fund: fund?.name ?? "",
        fund_type: fund?.type ?? "",
        fund_id: fund?.id ?? 0,
        fundClass: fund?.fundClass,
        name: value,
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
    });
}

function subsSelectMultipleEvent(data: SubscriptionEventData) {
    const { fund, shareClass, mode, cookied } = data;
    const value = `Select multiple funds: mode=${mode}, cookied=${Boolean(
        cookied,
    )}`;
    dispatchEvent({
        type: EventType.SubsSelectMultiple,
        value,
        fund: fund?.name ?? "",
        fund_type: fund?.type ?? "",
        fund_id: fund?.id ?? 0,
        name: value,
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
    });
}

function buttonClickEvent(
    data: EventData & {
        name?: string;
        details?: string;
        position?: string;
        docType?: string;
    },
) {
    const {
        fund,
        shareClass,
        details: value = "",
        name,
        position,
        docType,
    } = data;
    dispatchEvent({
        type: EventType.ButtonClick,
        value,
        fund: fund?.name ?? "",
        fund_type: fund?.type ?? "",
        fund_id: fund?.id ?? 0,
        fundClass: fund?.fundClass,
        name: name ?? "",
        shareclass: shareClass?.name,
        shareclass_code: shareClass?.code,
        position,
        docType,
    });
}

function accordionEvent(data: {
    name?: string;
    details?: string;
    type: "open" | "close";
    position?: string;
}) {
    const type =
        data.type === "open"
            ? EventType.AccordionOpen
            : EventType.AccordionClose;
    dispatchEvent({
        type,
        name: data?.name ?? "",
        position: data?.position ?? "",
        value: data?.details ?? "",
    });
}

function checkboxEvent(data: {
    name?: string;
    details?: string;
    position?: string;
}) {
    dispatchEvent({
        type: "Checkbox",
        name: data?.name ?? "",
        value: data?.details ?? "",
        position: data?.position ?? "",
    });
}

function inputFieldEvent(data: { name?: string }) {
    dispatchEvent({
        type: "fieldInteraction",
        name: data.name ?? "",
        value: data.name ?? "",
    });
}

declare global {
    interface Window {
        AppMeasurement?: any;
        s?: any;
        digitalData?: any;
    }
}

function pageLoadEvent(data: DigitalData) {
    try {
        const { s, AppMeasurement, digitalData } =
            typeof window !== "undefined"
                ? window
                : { s: null, AppMeasurement: null, digitalData: null };
        const canDoAnalytics =
            typeof AppMeasurement === "function" &&
            typeof s === "object" &&
            s instanceof window.AppMeasurement &&
            typeof digitalData === "object";

        if (!canDoAnalytics) {
            return;
        }
        const { pageName, customer } = data;
        if (pageName) {
            digitalData["cms"].pageName = pageName;
        }
        if (customer) {
            digitalData["customer"] = digitalData["customer"] ?? {};
            digitalData["customer"].emailAddress = customer.email;
            digitalData["customer"].forename = customer.firstName;
            digitalData["customer"].surname = customer.lastName;
            digitalData["customer"].company = customer.company;
            digitalData["customer"].jobTitle = customer.jobTitle;
        }
        s.t();
    } catch (err) {
        console.error(err);
    }
}

function lightboxEvent(name: string) {
    dispatchEvent({
        name,
        type: EventType.Lightbox,
        value: "",
    });
}

const useYardActionEvents = () => {
    return {
        filterEvent,
        groupEvent,
        searchEvent,
        downloadDataEvent,
        downloadDocumentEvent,
        tabEvent,
        expandCollapseEvent,
        clickLinkEvent,
        bulkDownloadEvent,
        sectionClickEvent,
        dropdownEvent,
        subsOverlayEvent,
        subsSelectMultipleEvent,
        buttonClickEvent,
        accordionEvent,
        checkboxEvent,
        inputFieldEvent,
        pageLoadEvent,
        lightboxEvent,
    };
};

export default useYardActionEvents;
